// src/components/dayClose/ConfettiCelebration.tsx
// Day Close 完成慶祝效果

import React, { useEffect, useRef } from 'react';

interface ConfettiCelebrationProps {
  trigger?: boolean;
}

export const ConfettiCelebration: React.FC<ConfettiCelebrationProps> = ({ trigger = true }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!trigger || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 設置 canvas 大小
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Confetti 粒子類
    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      color: string;
      rotation: number;
      rotationSpeed: number;
      gravity: number;

      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = -20;
        this.vx = (Math.random() - 0.5) * 6;
        this.vy = Math.random() * 3 + 2;
        this.radius = Math.random() * 3 + 2;
        this.color = [
          '#3B82F6', // blue
          '#10B981', // green
          '#F59E0B', // amber
          '#EF4444', // red
          '#8B5CF6', // purple
          '#EC4899'  // pink
        ][Math.floor(Math.random() * 6)];
        this.rotation = Math.random() * 360;
        this.rotationSpeed = (Math.random() - 0.5) * 10;
        this.gravity = 0.15;
      }

      update() {
        this.vy += this.gravity;
        this.y += this.vy;
        this.x += this.vx;
        this.rotation += this.rotationSpeed;
      }

      draw(ctx: CanvasRenderingContext2D) {
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate((this.rotation * Math.PI) / 180);

        // 繪製方形 confetti
        ctx.fillStyle = this.color;
        ctx.fillRect(-this.radius / 2, -this.radius / 2, this.radius, this.radius * 1.5);

        ctx.restore();
      }

      isOutOfBounds() {
        return this.y > canvas.height + 20;
      }
    }

    // 創建粒子
    const particles: Particle[] = [];
    for (let i = 0; i < 80; i++) {
      particles.push(new Particle());
    }

    // 動畫循環
    let animationId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 更新和繪製粒子
      for (let i = particles.length - 1; i >= 0; i--) {
        const particle = particles[i];
        particle.update();
        particle.draw(ctx);

        // 移除超出邊界的粒子
        if (particle.isOutOfBounds()) {
          particles.splice(i, 1);
        }
      }

      // 如果還有粒子，繼續動畫
      if (particles.length > 0) {
        animationId = requestAnimationFrame(animate);
      }
    };

    // 延遲啟動以配合其他動畫
    const startTimeout = setTimeout(() => {
      animate();
    }, 300);

    // 清理
    return () => {
      clearTimeout(startTimeout);
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [trigger]);

  if (!trigger) return null;

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-[9999]"
      style={{ mixBlendMode: 'normal' }}
    />
  );
};
